import axios from 'axios';
import { 
  AgentHealth, 
  QueryStatus, 
  SystemMetrics, 
  AgentCommunication,
  TaskDistribution 
} from '../types';

// Project and File types
export interface Project {
  project_id: string;
  name: string;
  description?: string;
  status: 'active' | 'archived' | 'deleted';
  created_at: string;
  updated_at: string;
  initial_query?: string;
  conversation_history: ConversationEntry[];
  files: ProjectFile[];
  metadata: Record<string, any>;
}

export interface ChatMessage {
  message_id: string;
  timestamp: string;
  message_type: 'user' | 'system' | 'assistant' | 'processing' | 'execution_plan';
  content: string;
  query_id?: string;
  metadata?: Record<string, any>;
}

export interface ConversationEntry {
  entry_id: string;
  project_id: string;
  timestamp: string;
  query: any;
  query_id?: string;
  result?: any;
  is_followup: boolean;
  parent_entry_id?: string;
  chat_messages?: ChatMessage[];
}

export interface ProjectFile {
  file_id: string;
  project_id: string;
  filename: string;
  file_path: string;
  file_size: number;
  content_type: string;
  uploaded_at: string;
  processed: boolean;
}

export interface FileMetadata {
  file_id: string;
  filename: string;
  file_path: string;
  file_size: number;
  content_type: string;
  scope: 'global' | 'project';
  category: string;
  status: string;
  uploaded_at: string;
  processed_at?: string;
  project_id?: string;
  tags: string[];
  chunk_count: number;
  vector_count: number;
  isDeleting?: boolean; // Added for UI state management
}

export interface FileStats {
  total_files: number;
  total_size_bytes: number;
  files_by_category: Record<string, number>;
  files_by_status: Record<string, number>;
  recent_uploads: FileMetadata[];
}

// Use runtime config if available, fallback to env vars or defaults
const getConfig = () => {
  if (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG) {
    return (window as any).RUNTIME_CONFIG;
  }
  return {
    API_URL: process.env.REACT_APP_API_URL?.trim() || 'http://api.fab-agents.columbus.tech',
    WS_URL: process.env.REACT_APP_WS_URL?.trim() || 'ws://api.fab-agents.columbus.tech'
  };
};

const config = getConfig();

class EnhancedAPIService {
  private ws: WebSocket | null = null
  private activeSSEConnections: Map<string, EventSource> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private apiClient: any;
  
  constructor() {
    // Use API URL from config or environment, default to MCP server
    const baseURL = config.API_URL?.trim() || process.env.REACT_APP_API_URL?.trim() || 'https://api.fab.columbus.tech';
    
    this.apiClient = axios.create({
      baseURL: baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    this.apiClient.interceptors.request.use((config: any) => {
      // Add authentication header for admin endpoints
      if (config.url && config.url.includes('/admin/')) {
        // For development, use a mock admin token
        // In production, this should come from a proper auth system
        const adminToken = localStorage.getItem('admin_token') || 'mock-admin-token-dev';
        config.headers.Authorization = `Bearer ${adminToken}`;
      }
      
      return config;
    });
    
    this.apiClient.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        console.error('Axios error:', {
          message: error.message,
          code: error.code,
          url: error.config?.url,
          response: error.response
        });
        return Promise.reject(error);
      }
    );
  }

  // WebSocket connection
  connectWebSocket(
    onAgentUpdate: (data: AgentHealth) => void,
    onCommunication: (data: AgentCommunication) => void,
    onQueryUpdate: (data: QueryStatus) => void,
    onTaskDistribution: (data: TaskDistribution) => void
  ) {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/api/v1/admin/ws`;
    
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('Connected to admin WebSocket');
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        switch (message.type) {
          case 'agent:health':
            onAgentUpdate(message.data);
            break;
          case 'agent:communication':
            onCommunication(message.data);
            break;
          case 'query:update':
            onQueryUpdate(message.data);
            break;
          case 'task:distribution':
            onTaskDistribution(message.data);
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.ws.onclose = () => {
      console.log('Disconnected from admin WebSocket');
      this.attemptReconnect(onAgentUpdate, onCommunication, onQueryUpdate, onTaskDistribution);
    };

    return this.ws;
  }

  private attemptReconnect(
    onAgentUpdate: (data: AgentHealth) => void,
    onCommunication: (data: AgentCommunication) => void,
    onQueryUpdate: (data: QueryStatus) => void,
    onTaskDistribution: (data: TaskDistribution) => void
  ) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Reconnecting... Attempt ${this.reconnectAttempts}`);
      
      setTimeout(() => {
        this.connectWebSocket(onAgentUpdate, onCommunication, onQueryUpdate, onTaskDistribution);
      }, this.reconnectDelay * this.reconnectAttempts);
    }
  }

  disconnectWebSocket() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  // Health endpoints - map to agents endpoint
  async getSystemHealth(): Promise<AgentHealth[]> {
    try {
      const response = await this.apiClient.get('/api/v1/agents/health');
      // The health endpoint returns data in the correct format
      return response.data.map((agent: any) => ({
        agentId: agent.agentId,
        agentName: agent.agentName,
        agentType: agent.agentType,
        status: agent.status === 'healthy' ? 'healthy' : 'unhealthy',
        lastHealthCheck: agent.lastHealthCheck,
        lastHeartbeat: agent.lastHealthCheck,
        uptime: agent.uptime,
        cpu: agent.cpu,
        memory: agent.memory,
        activeRequests: agent.activeRequests,
        vectorStore: agent.vectorStore
      }));
    } catch (error) {
      console.error('Failed to fetch system health:', error);
      return [];
    }
  }

  async getAgentHealth(agentId: string): Promise<AgentHealth> {
    const agents = await this.getSystemHealth();
    const agent = agents.find(a => a.agentId === agentId);
    if (!agent) {
      throw new Error(`Agent ${agentId} not found`);
    }
    return agent;
  }

  // Query management - updated to use new routes with approval workflow
  async submitQuery(query: string, context?: any): Promise<QueryStatus> {
    const response = await this.apiClient.post('/api/v1/queries', {
      query,
      context: {
        require_approval: true, // Default to requiring approval
        ...context // Allow context to override require_approval
      },
      debug: true,
    });
    return this.transformQueryStatus(response.data);
  }

  async getQueryStatus(queryId: string): Promise<QueryStatus> {
    // First try to get the full result (for completed queries)
    try {
      const response = await this.apiClient.get(`/api/v1/queries/${queryId}/full-result`);
      const data = response.data;
      
      // If this is a structured result (has areas/executive_summary), return it directly
      if (data.areas || data.executive_summary || data.sections) {
        console.log('✅ Successfully fetched full structured result for query:', queryId);
        return {
          queryId: queryId,
          status: 'completed',
          progress: 100,
          hasStructuredResponse: true,
          hasResult: true,
          areas: data.areas || data.sections || {},
          ...data  // Include all structured data
        } as any;
      }
      
      // If the query is completed but no structured data, transform normally
      if (data.status === 'completed' || data.status === 'failed') {
        console.log('Using full-result endpoint for completed query:', queryId);
        return this.transformQueryStatus(data);
      }
    } catch (error) {
      // If full-result fails or query is not complete, fall back to regular endpoint
      console.log('Full-result endpoint failed or query not complete, using regular endpoint');
    }
    
    // Fall back to regular endpoint for in-progress queries or if full-result fails
    const response = await this.apiClient.get(`/api/v1/queries/${queryId}`);
    return this.transformQueryStatus(response.data);
  }

  async getQueryFullResult(queryId: string): Promise<any> {
    // Public method to fetch full result data for a completed query
    const response = await this.apiClient.get(`/api/v1/queries/${queryId}/full-result`);
    return response.data;
  }

  async getActiveQueries(): Promise<QueryStatus[]> {
    const response = await this.apiClient.get('/api/v1/queries/active');
    return response.data.map((q: any) => this.transformQueryStatus(q));
  }

  async getQueryHistory(limit: number = 50): Promise<QueryStatus[]> {
    const response = await this.apiClient.get('/api/v1/queries/history', {
      params: { limit },
    });
    return response.data.map((q: any) => this.transformQueryStatus(q));
  }

  // Project management - Real implementation using axios (which works!)
  async createProject(name: string, description?: string, initial_query?: string): Promise<Project> {
    const response = await this.apiClient.post('/api/v1/projects/', {
      name,
      description,
      initial_query,
      metadata: {}
    });
    return response.data;
  }

  async listProjects(status?: string, limit: number = 100): Promise<Project[]> {
    const params: any = { limit };
    if (status) params.status = status;
    
    const response = await this.apiClient.get('/api/v1/projects/', { params });
    return response.data;
  }

  async getProject(projectId: string): Promise<Project> {
    try {
      const response = await this.apiClient.get(`/api/v1/projects/${projectId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get project:', projectId, error);
      throw error;
    }
  }

  async updateProject(projectId: string, updates: Partial<Project>): Promise<Project> {
    const response = await this.apiClient.put(`/api/v1/projects/${projectId}`, updates);
    return response.data;
  }

  async submitProjectQuery(
    projectId: string, 
    query: string, 
    isFollowup: boolean = false,
    parentEntryId?: string
  ): Promise<any> {
    const response = await this.apiClient.post(`/api/v1/projects/${projectId}/query`, {
      project_id: projectId,
      query,
      is_followup: isFollowup,
      parent_entry_id: parentEntryId,
      include_project_files: true,
      additional_context: {
        // Don't override the backend's approval logic
      }
    });
    return response.data;
  }

  async refreshProject(projectId: string, includeFollowups: boolean = true): Promise<any[]> {
    const response = await this.apiClient.post(`/api/v1/projects/${projectId}/refresh`, {
      project_id: projectId,
      include_followups: includeFollowups,
      use_cached_results: false,
    });
    return response.data;
  }

  async getProjectHistory(projectId: string, limit: number = 100): Promise<ConversationEntry[]> {
    const response = await this.apiClient.get(`/api/v1/projects/${projectId}/history`, {
      params: { limit },
    });
    return response.data;
  }

  async saveChatMessage(
    projectId: string, 
    entryId: string, 
    messageType: string, 
    content: string, 
    queryId?: string,
    metadata?: Record<string, any>
  ): Promise<{ message_id: string; timestamp: string; status: string }> {
    const response = await this.apiClient.post(`/api/v1/projects/${projectId}/chat-messages`, {
      entry_id: entryId,
      message_type: messageType,
      content,
      query_id: queryId,
      metadata: metadata || {}
    });
    return response.data;
  }

  async deleteProject(projectId: string, hardDelete: boolean = false): Promise<void> {
    await this.apiClient.delete(`/api/v1/projects/${projectId}`, {
      params: { hard_delete: hardDelete },
    });
  }

  // File management - NEW
  async uploadFile(
    file: File,
    scope: 'global' | 'project' = 'global',
    projectId?: string,
    category?: string,
    tags?: string[]
  ): Promise<FileMetadata> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', scope);
    if (projectId) formData.append('project_id', projectId);
    if (category) formData.append('category', category);
    if (tags) formData.append('tags', tags.join(','));

    const response = await this.apiClient.post('/api/v1/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadFileWithProgress(
    file: File,
    scope: 'global' | 'project' = 'global',
    projectId?: string,
    category?: string,
    onProgress?: (progress: number) => void
  ): Promise<FileMetadata> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', scope);
    if (projectId) formData.append('project_id', projectId);
    if (category) formData.append('category', category);

    const response = await this.apiClient.post('/api/v1/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  }

  async listFiles(
    scope?: 'global' | 'project',
    projectId?: string,
    category?: string,
    status?: string,
    limit: number = 100
  ): Promise<FileMetadata[]> {
    const response = await this.apiClient.get('/api/v1/files/', {
      params: { scope, project_id: projectId, category, status, limit },
    });
    return response.data;
  }

  async getFileStats(): Promise<FileStats> {
    const response = await this.apiClient.get('/api/v1/files/stats');
    return response.data;
  }

  async getFileMetadata(fileId: string): Promise<FileMetadata> {
    const response = await this.apiClient.get(`/api/v1/files/${fileId}`);
    return response.data;
  }

  async downloadFile(fileId: string): Promise<Blob> {
    const response = await this.apiClient.get(`/api/v1/files/${fileId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteFile(fileId: string, hardDelete: boolean = false): Promise<void> {
    await this.apiClient.delete(`/api/v1/files/${fileId}`, {
      params: { hard_delete: hardDelete },
    });
  }

  async processFile(fileId: string, forceReprocess: boolean = false): Promise<any> {
    const response = await this.apiClient.post(`/api/v1/files/${fileId}/process`, null, {
      params: { force_reprocess: forceReprocess },
    });
    return response.data;
  }

  async uploadProjectFile(projectId: string, file: File, category?: string, tags?: string[]): Promise<FileMetadata> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', 'project');
    formData.append('project_id', projectId);
    if (category) formData.append('category', category);
    if (tags) formData.append('tags', tags.join(','));

    const response = await this.apiClient.post('/api/v1/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    const metadata = response.data;
    
    // Automatically process the uploaded file
    try {
      await this.processFile(metadata.file_id);
    } catch (error) {
      console.warn('Failed to auto-process uploaded file:', error);
      // Don't throw - file is uploaded successfully, processing can happen later
    }
    
    return metadata;
  }

  async uploadProjectFileWithGlobalSync(projectId: string, file: File, category?: string, tags?: string[]): Promise<{projectFile: FileMetadata, globalFile?: FileMetadata}> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    if (category) formData.append('category', category);
    if (tags) formData.append('tags', tags.join(','));

    const response = await this.apiClient.post('/api/v1/files/upload-project-with-global', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    const result = response.data;
    
    // Process both files if they exist
    try {
      await this.processFile(result.project_file.file_id);
      if (result.global_file) {
        await this.processFile(result.global_file.file_id);
      }
    } catch (error) {
      console.warn('Failed to auto-process uploaded files:', error);
    }
    
    return {
      projectFile: result.project_file,
      globalFile: result.global_file
    };
  }

  async listProjectFiles(projectId: string, status?: string, limit: number = 100): Promise<FileMetadata[]> {
    const response = await this.apiClient.get(`/api/v1/files/project/${projectId}`, {
      params: { status, limit },
    });
    return response.data;
  }

  // Agent communications
  async getRecentCommunications(limit: number = 100): Promise<AgentCommunication[]> {
    try {
      const response = await this.apiClient.get('/api/v1/admin/communications', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.warn('Communications endpoint not available:', error);
      return [];
    }
  }

  async getCommunicationsByQuery(queryId: string): Promise<AgentCommunication[]> {
    const response = await this.apiClient.get(`/api/v1/queries/${queryId}/communications`);
    return response.data;
  }

  // Metrics - Real implementation
  async getSystemMetrics(): Promise<SystemMetrics> {
    try {
      const response = await this.apiClient.get('/api/v1/agents/metrics');
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch system metrics, using defaults:', error);
      // Return default values if metrics endpoint not available
      return {
        totalQueries: 0,
        successfulQueries: 0,
        failedQueries: 0,
        avgResponseTime: 0,
        totalTokensUsed: 0,
        activeAgents: 0,
        queuedTasks: 0,
        completedTasks: 0,
        systemUptime: 0,
        lastUpdated: new Date().toISOString()
      };
    }
  }

  async getTokenUsage(period: 'hour' | 'day' | 'week' | 'month' = 'day'): Promise<any> {
    const response = await this.apiClient.get('/api/v1/agents/metrics/tokens', {
      params: { period },
    });
    return response.data;
  }

  async getAgentMetrics(agentId: string, period: string = 'day'): Promise<any> {
    const response = await this.apiClient.get(`/api/v1/agents/metrics/${agentId}`, {
      params: { period },
    });
    return response.data;
  }

  // Task distribution - updated route
  async getTaskDistribution(queryId: string): Promise<TaskDistribution[]> {
    const response = await this.apiClient.get(`/api/v1/queries/${queryId}/tasks`);
    return response.data;
  }

  // Agent control - admin only
  async restartAgent(agentId: string): Promise<void> {
    await this.apiClient.post(`/api/v1/admin/agents/${agentId}/restart`);
  }

  async scaleAgent(agentId: string, replicas: number): Promise<void> {
    await this.apiClient.post(`/api/v1/admin/agents/${agentId}/scale`, { replicas });
  }

  // Data management - admin only
  async clearCache(agentId?: string): Promise<void> {
    const url = agentId 
      ? `/api/v1/admin/cache/clear/${agentId}`
      : '/api/v1/admin/cache/clear';
    await this.apiClient.post(url);
  }

  // SSE for query streaming
  streamQueryStatus(queryId: string, onUpdate: (data: any) => void): EventSource {
    // Close any existing connection for this query
    if (this.activeSSEConnections.has(queryId)) {
      console.log(`Closing existing SSE connection for query ${queryId}`);
      this.activeSSEConnections.get(queryId)?.close();
      this.activeSSEConnections.delete(queryId);
    }

    // EventSource needs the full URL - use the same baseURL as other API calls
    const baseURL = config.API_URL || process.env.REACT_APP_API_URL || 'https://api.fab.columbus.tech';
    const url = `${baseURL}/api/v1/queries/${queryId}/stream`;
    const eventSource = new EventSource(url);
    
    // Store the connection
    this.activeSSEConnections.set(queryId, eventSource);
    
    // Default message handler: log only to avoid double-dispatch.
    // Some servers emit both a default message and a typed 'update' event.
    // We only process typed events ('update' and 'done') to prevent duplicates.
    eventSource.onmessage = (event) => {
      console.log('SSE default message:', event.data);
      // Intentionally not calling onUpdate here to avoid duplicate updates
    };
    
    eventSource.addEventListener('update', (event) => {
      console.log('SSE update event:', event.data);
      try {
        const data = JSON.parse(event.data);
        
        // Check if the response was truncated
        if (data._truncated) {
          console.warn('Response was truncated, fetching full result...');
          // Fetch the full result using the non-streaming endpoint
          this.apiClient.get(`/api/v1/queries/${queryId}/full-result`)
            .then((response: any) => {
              onUpdate(response.data);
            })
            .catch((error: any) => {
              console.error('Failed to fetch full result:', error);
              // Fall back to truncated data
              onUpdate(data);
            });
        } else {
          onUpdate(data);
        }
      } catch (error) {
        console.error('Error parsing SSE update:', error);
      }
    });
    
    eventSource.addEventListener('done', (event) => {
      console.log('SSE done event:', event.data);
      try {
        const data = JSON.parse(event.data);
        
        // The done event is just a signal that the stream is complete
        // The actual completion data should have been sent in the update event
        // We don't need to call onUpdate again here to avoid duplicate processing
        console.log('SSE stream completed for query:', queryId);
        
        // Clean up the connection
        this.activeSSEConnections.delete(queryId);
        setTimeout(() => eventSource.close(), 50);
      } catch (error) {
        console.error('Error parsing SSE done:', error);
        // Clean up the connection on error too
        this.activeSSEConnections.delete(queryId);
        eventSource.close();
      }
    });
    
    eventSource.addEventListener('ping', (event) => {
      console.log('SSE ping:', event.data);
    });
    
    eventSource.onerror = (error) => {
      // Avoid spamming console with transient errors
      console.warn('SSE error (will auto-close if not found):', error);
      if (eventSource.readyState === EventSource.CLOSED) {
        console.log('SSE connection closed');
        // Clean up the connection
        this.activeSSEConnections.delete(queryId);
      }
    };
    
    return eventSource;
  }

  // Close all active SSE connections
  closeAllSSEConnections(): void {
    console.log(`Closing ${this.activeSSEConnections.size} active SSE connections`);
    this.activeSSEConnections.forEach((eventSource, queryId) => {
      console.log(`Closing SSE connection for query ${queryId}`);
      eventSource.close();
    });
    this.activeSSEConnections.clear();
  }

  // Close specific SSE connection
  closeSSEConnection(queryId: string): void {
    if (this.activeSSEConnections.has(queryId)) {
      console.log(`Closing SSE connection for query ${queryId}`);
      this.activeSSEConnections.get(queryId)?.close();
      this.activeSSEConnections.delete(queryId);
    }
  }

  // Approval handling methods
  async approveExecutionPlan(queryId: string): Promise<void> {
    await this.apiClient.post(`/api/v1/queries/${queryId}/approve`, {
      approved: true
    });
  }

  async rejectExecutionPlan(queryId: string, feedback: string): Promise<void> {
    await this.apiClient.post(`/api/v1/queries/${queryId}/approve`, {
      approved: false,
      feedback
    });
  }

  // Follow-up queries
  async submitFollowUpQuery(originalQueryId: string, query: string): Promise<QueryStatus> {
    const response = await this.apiClient.post(`/api/v1/queries/${originalQueryId}/followup`, {
      query
    });
    // Ensure we return a shape consistent with QueryStatus consumers
    const data = response.data;
    return {
      queryId: data.queryId,
      query: data.query,
      status: data.status,
      currentAgent: data.currentAgent || data.currentStep,
      progress: data.progress || 0,
      startTime: data.startTime,
      endTime: data.endTime,
      steps: data.steps || [],
      result: data.result || data.structured_response,
      executionPlan: data.executionPlan,
      reasoning: data.reasoning,
      error: data.error,
      context: data.context,
      structured_response: data.structured_response,
      final_response: data.final_response
    } as QueryStatus;
  }

  // Helper to transform query status to expected format
  private transformQueryStatus(data: any): QueryStatus {
    return {
      queryId: data.queryId,
      query: data.query,
      status: data.status,
      currentAgent: data.currentAgent || data.currentStep,
      progress: data.progress || 0,
      startTime: data.startTime,
      endTime: data.endTime,
      steps: data.steps || [],
      result: data.result,
      structured_response: data.structured_response,  // Add this field
      executionPlan: data.executionPlan,
      reasoning: data.reasoning,
      error: data.error,
      context: data.context
    };
  }

  // Admin File Management Operations
  async getFileProcessingStatus(): Promise<FileProcessingStatus> {
    const response = await this.apiClient.get('/api/v1/admin/files/processing-status');
    return response.data;
  }

  async triggerFileProcessing(limit: number = 5): Promise<FileProcessingResult> {
    const response = await this.apiClient.post(`/api/v1/admin/files/process?limit=${limit}`);
    return response.data;
  }

  async resetFileStatus(params: {
    statusFilter?: string;
    targetStatus?: string;
    fileIds?: string[];
    limit?: number;
  }): Promise<ResetFileStatusResult> {
    const queryParams = new URLSearchParams();
    if (params.statusFilter) queryParams.append('status_filter', params.statusFilter);
    if (params.targetStatus) queryParams.append('target_status', params.targetStatus);
    if (params.fileIds) {
      params.fileIds.forEach(id => queryParams.append('file_ids', id));
    }
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await this.apiClient.post(`/api/v1/admin/files/reset-status?${queryParams.toString()}`);
    return response.data;
  }

  async clearFileVectors(fileIds?: string[], clearAll: boolean = false): Promise<ClearVectorsResult> {
    const queryParams = new URLSearchParams();
    if (fileIds) {
      fileIds.forEach(id => queryParams.append('file_ids', id));
    }
    if (clearAll) queryParams.append('clear_all', 'true');

    const response = await this.apiClient.delete(`/api/v1/admin/files/vectors?${queryParams.toString()}`);
    return response.data;
  }

  async generateProfessionalPDF(queryData: any, options: any): Promise<{ pdf_base64: string; filename: string }> {
    try {
      const response = await this.apiClient.post('/api/v1/pdf/generate-professional', {
        query_data: queryData,
        options: options
      });
      return response.data;
    } catch (error) {
      console.error('Error generating professional PDF:', error);
      throw error;
    }
  }

  async generateFABPDF(queryData: any, options: any): Promise<{ pdf_base64: string; filename: string }> {
    try {
      const response = await this.apiClient.post('/api/v1/pdf/generate-fab', {
        query_data: queryData,
        options: options
      });
      return response.data;
    } catch (error) {
      console.error('Error generating FAB PDF:', error);
      throw error;
    }
  }

  async generateColumbusPDF(queryData: any, options: any): Promise<{ pdf_base64: string; filename: string }> {
    try {
      const response = await this.apiClient.post('/api/v1/pdf/generate-columbus', {
        query_data: queryData,
        options: options
      });
      return response.data;
    } catch (error) {
      console.error('Error generating Columbus PDF:', error);
      throw error;
    }
  }
}

// New type definitions for file management
export interface FileProcessingStatus {
  configuration: {
    background_processing_enabled: boolean;
    pause_during_queries: boolean;
    batch_size: number;
    timeout_seconds: number;
  };
  status: {
    files_pending_processing: number;
    files_processed: number;
    files_failed: number;
  };
  pending_files: Array<{
    file_id: string;
    filename: string;
    uploaded_at: string;
  }>;
}

export interface FileProcessingResult {
  status: string;
  files_processed: number;
  results: Array<{
    file_id: string;
    filename: string;
    status: string;
    vectors_indexed?: number;
    chunks_created?: number;
    error?: string;
  }>;
}

export interface ResetFileStatusResult {
  status: string;
  reset_count: number;
  target_status: string;
  reset_files: Array<{
    file_id: string;
    filename: string;
    previous_status: string;
    new_status: string;
  }>;
}

export interface ClearVectorsResult {
  status: string;
  cleared_count?: number;
  message: string;
}

const enhancedAPI = new EnhancedAPIService();
export default enhancedAPI;